"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Deployment detector that automatically reloads the page when a new deployment is detected.
 * 
 * How it works:
 * 1. Polls /api/build-version every 30 seconds
 * 2. Compares current build ID with initial build ID
 * 3. Reloads page when build ID changes
 * 4. Only polls when tab is visible (saves resources)
 */

const POLL_INTERVAL = 10000; // 30 seconds
const RELOAD_DELAY = 2000; // 2 seconds notification before reload

export default function DeploymentDetector() {
    const [initialBuildId, setInitialBuildId] = useState<string | null>(null);
    const [showNotification, setShowNotification] = useState(false);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isCheckingRef = useRef(false);

    // Fetch current build ID
    const checkForNewDeployment = async () => {
        // Prevent concurrent checks
        if (isCheckingRef.current) return;

        isCheckingRef.current = true;

        try {
            console.warn(`[DeploymentDetector] 🔍 Consultando API /api/build-version... (${new Date().toLocaleTimeString()})`);

            const response = await fetch("/api/build-version", {
                cache: "no-store",
                headers: {
                    "Cache-Control": "no-cache",
                },
            });

            if (!response.ok) {
                console.warn("[DeploymentDetector] ❌ Failed to fetch build version");
                return;
            }

            // Verificar que la respuesta sea JSON antes de parsear
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.warn("[DeploymentDetector] ❌ Response is not JSON, received:", text.substring(0, 100));
                return;
            }

            const data = await response.json();
            const currentBuildId = data.buildId;

            console.warn(`[DeploymentDetector] 📦 Build ID recibido: "${currentBuildId}"`);

            // Store initial build ID on first check
            if (!initialBuildId) {
                setInitialBuildId(currentBuildId);
                console.warn(`[DeploymentDetector] ✅ Build ID inicial guardado: "${currentBuildId}"`);
                return;
            }

            // Log comparison
            console.warn(`[DeploymentDetector] 🔄 Comparando builds:`);
            console.warn(`  - Build anterior: "${initialBuildId}"`);
            console.warn(`  - Build actual:   "${currentBuildId}"`);

            // Check if build ID has changed
            if (currentBuildId !== initialBuildId) {
                console.warn(
                    "%c[DeploymentDetector] 🚀 ¡NUEVO DEPLOYMENT DETECTADO! Aquí tuvo que haber recargado",
                    "background: #10b981; color: white; font-weight: bold; padding: 4px 8px; border-radius: 4px;",
                    {
                        buildAnterior: initialBuildId,
                        buildNuevo: currentBuildId
                    }
                );

                // Show notification
                setShowNotification(true);

                // Reload after delay
                setTimeout(() => {
                    console.warn("[DeploymentDetector] 🔄 Recargando página ahora...");
                    window.location.reload();
                }, RELOAD_DELAY);
            } else {
                console.warn(`[DeploymentDetector] ✓ Sin cambios, mismo build ID`);
            }
        } catch (error) {
            console.error("[DeploymentDetector] ❌ Error checking for deployment:", error);
        } finally {
            isCheckingRef.current = false;
        }
    };

    // Start polling
    useEffect(() => {
        // Initial check
        checkForNewDeployment();

        // Set up polling interval
        pollIntervalRef.current = setInterval(() => {
            // Only check if tab is visible
            if (!document.hidden) {
                checkForNewDeployment();
            }
        }, POLL_INTERVAL);

        // Handle visibility change - check immediately when tab becomes visible
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkForNewDeployment();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Cleanup
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [initialBuildId]);

    // Render notification if new deployment detected
    if (showNotification) {
        return (
            <div
                style={{
                    position: "fixed",
                    top: "20px",
                    right: "20px",
                    backgroundColor: "#10b981",
                    color: "white",
                    padding: "16px 24px",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    zIndex: 10000,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    fontSize: "14px",
                    fontWeight: "500",
                }}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ animation: "spin 1s linear infinite" }}
                >
                    <path
                        d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                </svg>
                Nueva versión disponible. Recargando...
                <style>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
            </div>
        );
    }

    return null;
}
